import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter, Package, X, Plus, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import PromoCodeInput from '../components/PromoCodeInput';
import { fetchProducts } from '../data/products';
import { useCartStore } from '../store/cartStore';
import { trackPageView, trackEvent } from '../lib/analytics';
import { formatPrice, stripHtmlToText } from '../lib/utils';
import { useCMS } from '../hooks/useCMS';
import { apiFetchJson } from '../lib/api';
import type { Product, PackageConfigPublic } from '../types';

const BRAND_GROUP_OPTIONS = [
  { slug: 'hk_brand', name: '香港品牌' },
  { slug: 'china_brand', name: '中國品牌' },
  { slug: 'overseas_brand', name: '海外品牌' },
];

// Restaurant offers moved to the dedicated /offers page — never show them in the store.
const STORE_EXCLUDED_BRAND_GROUPS = ['local_restaurant', 'mainland_restaurant'];

// Fallback only — live labels come from /api/public/product-categories (DB-driven)
const CATEGORY_LABELS: Record<string, string> = {
  network: '網絡設備',
  computer: '電腦配件',
  tools: '工具儀器',
  devboards: '開發板',
  automation: '感應器／自動化',
};

// Seeded PRNG so the random order stays stable during the current page session.
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DAY_SECONDS = 86400;

function getProductWeight(createdAt?: number): number {
  if (!createdAt) return 1;
  const ageDays = (Math.floor(Date.now() / 1000) - createdAt) / DAY_SECONDS;
  if (ageDays <= 7) return 5;
  if (ageDays <= 30) return 2;
  return 1;
}

function weightedShuffle<T>(items: T[], weightFn: (item: T) => number, seed: number): T[] {
  const rng = mulberry32(seed);
  const scored = items.map((item) => ({
    item,
    score: -Math.log(Math.max(rng(), 1e-10)) / weightFn(item),
  }));
  scored.sort((a, b) => a.score - b.score);
  return scored.map((s) => s.item);
}

export default function Store() {
  const [products, setProducts] = useState<Product[]>([]);
  const [packages, setPackages] = useState<PackageConfigPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandGroupFilter, setBrandGroupFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState<'all' | 'new' | 'secondhand'>('all');
  // 關鍵字搜尋（2026-10-21 Kevin 需求：打「記憶體」就過濾出所有記憶體產品）
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const { getValue: getCmsValue } = useCMS();
  const [cartOpen, setCartOpen] = useState(false);
  const [categoryLabels, setCategoryLabels] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [shuffleSeed, setShuffleSeed] = useState(() => Date.now());
  const ITEMS_PER_PAGE = 20;
  // 套餐選配 modal：{ pkg, picks: Record<groupName, Set<productId>> }
  const [picking, setPicking] = useState<{ pkg: PackageConfigPublic; picks: Record<string, Set<number>> } | null>(null);

  const totalItems = useCartStore((s) => s.totalItems());
  const subtotal = useCartStore((s) => s.subtotal());
  const addPackage = useCartStore((s) => s.addPackage);

  useEffect(() => {
    trackPageView('/store');
    // Products arrive already brandified («X-1493») from the fetchProducts
    // data layer — no per-page transform needed here.
    fetchProducts().then((data) => {
      setProducts(data);
      // 套餐（公開端點；失敗靜默——套餐區塊不顯示即可）
      apiFetchJson<PackageConfigPublic[]>('/public/package-configs')
        .then((res) => { if (res.success && Array.isArray(res.data)) setPackages(res.data); })
        .catch(() => {});
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // 分類顯示名由 DB 驅動（後台新增分類即生效）；失敗靜默退回 fallback map
    apiFetchJson<{ slug: string; name: string }[]>('/public/product-categories')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setCategoryLabels(Object.fromEntries(res.data.map((c) => [c.slug, c.name])));
        }
      })
      .catch(() => {});
  }, []);

  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      if (brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter) {
        if (p.brand) set.add(p.brand);
      }
    });
    return Array.from(set).sort();
  }, [products, brandGroupFilter]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => {
      if (p.category) map.set(p.category, categoryLabels[p.category] || CATEGORY_LABELS[p.category] || p.category);
    });
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [products, categoryLabels]);

  const noFiltersActive =
    brandGroupFilter === 'all' && brandFilter === 'all' && categoryFilter === 'all' && conditionFilter === 'all' && !searchQuery.trim();

  const sortedProducts = useMemo(() => {
    if (noFiltersActive) {
      return weightedShuffle(products, (p) => getProductWeight(p.createdAt), shuffleSeed);
    }
    return [...products].sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
  }, [products, noFiltersActive, shuffleSeed]);

  const filteredProducts = sortedProducts.filter((p) => {
    if (STORE_EXCLUDED_BRAND_GROUPS.includes(p.brandGroup || '')) return false;
    const groupMatch = brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter;
    const brandMatch = brandFilter === 'all' || p.brand === brandFilter;
    const categoryMatch = categoryFilter === 'all' || p.category === categoryFilter;
    // 二手過濾（2026-10-20 Kevin 需求）：secondhand＝只睇二手；new＝只睇全新
    const conditionMatch =
      conditionFilter === 'all' ||
      (conditionFilter === 'secondhand' ? p.isSecondhand === 1 : p.isSecondhand !== 1);
    // 關鍵字：名稱／描述（剝 HTML）／品牌／SKU／類別顯示名，中英文 substring 都食
    const q = searchQuery.trim().toLowerCase();
    const haystack = [
      p.name,
      p.brand,
      p.sku,
      stripHtmlToText(p.description || ''),
      categoryLabels[p.category] || p.category,
    ].join(' ').toLowerCase();
    const searchMatch = !q || haystack.includes(q);
    return groupMatch && brandMatch && categoryMatch && conditionMatch && searchMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [brandGroupFilter, brandFilter, categoryFilter, conditionFilter, searchQuery, shuffleSeed]);

  // 套餐加入：有自選組→開 modal；純必含→直接入車
  const handleAddPackage = (pkg: PackageConfigPublic) => {
    trackEvent('package_add_to_cart', { packageKey: pkg.key, hasChoiceGroups: pkg.choiceGroups.length > 0 });
    if (pkg.choiceGroups.length === 0) {
      addPackage(pkg, [], '');
      setCartOpen(true);
      return;
    }
    const picks: Record<string, Set<number>> = {};
    pkg.choiceGroups.forEach((g) => { picks[g.groupName] = new Set(); });
    setPicking({ pkg, picks });
  };

  const togglePick = (groupName: string, productId: number, chooseCount: number) => {
    setPicking((prev) => {
      if (!prev) return prev;
      const set = new Set(prev.picks[groupName]);
      if (set.has(productId)) set.delete(productId);
      else {
        if (set.size >= chooseCount) return prev; // 最多選 N
        set.add(productId);
      }
      return { ...prev, picks: { ...prev.picks, [groupName]: set } };
    });
  };

  const allGroupsSatisfied = picking
    ? picking.pkg.choiceGroups.every((g) => picking.picks[g.groupName]?.size === g.chooseCount)
    : false;

  const confirmPicking = () => {
    if (!picking || !allGroupsSatisfied) return;
    const selections: { productId: number; productName: string | null; groupName: string }[] = [];
    const parts: string[] = [];
    picking.pkg.choiceGroups.forEach((g) => {
      const chosen = g.options.filter((o) => picking.picks[g.groupName]?.has(o.productId));
      chosen.forEach((o) => selections.push({ productId: o.productId, productName: o.productName, groupName: g.groupName }));
      parts.push(`${g.groupName}：${chosen.map((o) => o.productName).join('＋')}`);
    });
    addPackage(picking.pkg, selections, parts.join('｜'));
    setPicking(null);
    setCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} />

      {/* 套餐自選組 modal */}
      {picking && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div>
                <h3 className="font-bold text-lg">{picking.pkg.name}</h3>
                <p className="text-sm text-stone-500">{formatPrice(picking.pkg.priceCents)} · 完成所有選擇後加入</p>
              </div>
              <button onClick={() => setPicking(null)} className="text-stone-400 hover:text-stone-600"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-5">
              {picking.pkg.choiceGroups.map((g) => (
                <div key={g.groupName}>
                  <p className="font-bold text-sm mb-2">
                    {g.groupName} <span className="text-x1493-primary">（任選 {g.chooseCount}）</span>
                    <span className="ml-2 text-stone-400 font-normal">已選 {picking.picks[g.groupName]?.size || 0}/{g.chooseCount}</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {g.options.map((o) => {
                      const picked = picking.picks[g.groupName]?.has(o.productId);
                      return (
                        <button
                          key={o.productId}
                          onClick={() => togglePick(g.groupName, o.productId, g.chooseCount)}
                          className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition ${
                            picked
                              ? 'border-x1493-primary bg-red-50 text-x1493-primary'
                              : 'border-stone-200 text-stone-600 hover:border-stone-300'
                          }`}
                        >
                          {o.productName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button
                onClick={confirmPicking}
                disabled={!allGroupsSatisfied}
                className="w-full bg-x1493-primary text-white py-3 rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {allGroupsSatisfied ? '加入購物車' : '請完成所有選擇'}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="relative bg-x1493-dark text-white py-12 md:py-16 overflow-hidden">
        {getCmsValue('store.hero.image', '') && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${getCmsValue('store.hero.image', '')}')` }}
          />
        )}
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{getCmsValue('store.title', 'Kare Tech 商店')}</h1>
          <p className="text-white/80">{getCmsValue('store.subtitle', 'IT 產品與工程器材，一鍵加入購物車')}</p>
        </div>
      </section>

      <section className="py-8 sticky top-[72px] z-30 bg-white/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[12rem]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋產品關鍵字（例：記憶體、DDR5、Cisco）"
                className="w-full pl-9 pr-8 p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none bg-white"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  aria-label="清除搜尋"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <Filter size={18} className="text-stone-500" />
            <select
              value={brandGroupFilter}
              onChange={(e) => {
                setBrandGroupFilter(e.target.value);
                setBrandFilter('all');
              }}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部品牌群組</option>
              {BRAND_GROUP_OPTIONS.map((b) => (
                <option key={b.slug} value={b.slug}>{b.name}</option>
              ))}
            </select>
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部品牌</option>
              {brandOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部類別</option>
              {categoryOptions.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value as 'all' | 'new' | 'secondhand')}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全新＋二手</option>
              <option value="new">全新</option>
              <option value="secondhand">♻️ 二手</option>
            </select>
            {noFiltersActive && (
              <button
                onClick={() => setShuffleSeed(Date.now())}
                className="flex items-center gap-1.5 text-sm font-bold text-x1493-primary border border-x1493-primary px-3 py-2 rounded-xl hover:bg-x1493-primary hover:text-white transition"
                title="隨機洗牌，新產品權重更高"
              >
                <span>火熱抽籤</span>
                <span>🎲</span>
              </button>
            )}
          </div>

          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center justify-center gap-2 w-full sm:w-auto sm:self-end bg-x1493-primary text-white px-5 py-2.5 rounded-xl font-bold hover:bg-red-700 transition"
          >
            <ShoppingCart size={20} />
            <span>購物車</span>
            {totalItems > 0 && (
              <span className="bg-white text-x1493-primary text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
            <span className="hidden sm:inline">· {formatPrice(subtotal)}</span>
          </button>
        </div>
      </section>

      {/* 套餐區塊（有上架套餐時顯示） */}
      {packages.length > 0 && (
        <section className="py-8 border-b border-stone-100">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Package className="text-x1493-primary" size={24} /> 套餐推介</h2>
            <p className="text-sm text-stone-500 mb-6">組合優惠價，一次買齊</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((pkg) => (
                <div key={pkg.id} className="border-2 border-stone-200 rounded-2xl overflow-hidden flex hover:border-x1493-primary/50 transition bg-white">
                  <div className="w-32 sm:w-40 bg-stone-50 flex-shrink-0 aspect-square">
                    {pkg.imageUrl ? (
                      <img src={pkg.imageUrl} alt={pkg.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300"><Package size={40} /></div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col">
                    <h3 className="font-bold text-lg">{pkg.name}</h3>
                    {pkg.description && <p className="text-sm text-stone-500 line-clamp-2 mt-1">{pkg.description}</p>}
                    <div className="text-xs text-stone-500 mt-2 space-y-1 flex-1">
                      {pkg.fixedItems.length > 0 && (
                        <p>📦 必含：{pkg.fixedItems.map((f) => `${f.productName || ''}${f.quantity > 1 ? `×${f.quantity}` : ''}`).join('、')}</p>
                      )}
                      {pkg.choiceGroups.map((g) => (
                        <p key={g.groupName}>🎯 {g.groupName}：任選 {g.chooseCount}（{g.options.map((o) => o.productName).join(' / ')}）</p>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xl font-bold text-x1493-primary">{formatPrice(pkg.priceCents)}</p>
                      <button
                        onClick={() => handleAddPackage(pkg)}
                        className="bg-x1493-primary text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition flex items-center gap-1.5 text-sm"
                      >
                        <Plus size={16} /> 加入套餐
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {loading ? (
                <div className="text-center py-20">載入中...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16 bg-stone-50 rounded-2xl">
                  <p className="text-stone-500 mb-2">
                    {searchQuery.trim() ? `搵唔有「${searchQuery.trim()}」相關嘅產品` : '呢個篩選組合冇產品'}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setBrandGroupFilter('all'); setBrandFilter('all'); setCategoryFilter('all'); setConditionFilter('all'); }}
                    className="text-x1493-primary font-bold hover:underline"
                  >
                    清除全部篩選 →
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {paginatedProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    上一頁
                  </button>
                  <span className="text-sm text-stone-600">
                    第 {currentPage} / {totalPages} 頁（共 {filteredProducts.length} 件）
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    下一頁
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-stone-50 rounded-2xl p-6 sticky top-40">
                <h3 className="font-bold text-lg mb-4">購物車摘要</h3>
                {totalItems === 0 ? (
                  <p className="text-stone-500 text-sm mb-4">購物車尚無商品</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">件數</span>
                      <span className="font-semibold">{totalItems}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>小計</span>
                      <span className="text-x1493-primary">{formatPrice(subtotal)}</span>
                    </div>
                  </div>
                )}

                <PromoCodeInput orderAmount={subtotal} />

                <Link
                  to="/checkout"
                  className={`block w-full text-center mt-6 py-3 rounded-xl font-bold transition ${
                    totalItems > 0
                      ? 'bg-x1493-primary text-white hover:bg-red-700'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed pointer-events-none'
                  }`}
                >
                  前往結帳
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
